import React from 'react';
import { TooltipRenderProps } from 'react-joyride';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { X, ArrowLeft, ArrowRight, SkipForward } from 'lucide-react';

interface CustomTooltipProps extends TooltipRenderProps {
  totalSteps?: number;
}

export const CustomTooltip: React.FC<CustomTooltipProps> = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  totalSteps = 1,
}) => {
  const progress = ((index + 1) / totalSteps) * 100;

  return (
    <div
      {...tooltipProps}
      className="bg-background border border-border rounded-lg shadow-lg max-w-sm p-0 overflow-hidden"
    >
      {/* Header with progress */}
      <div className="bg-primary/5 px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-muted-foreground">
            Step {index + 1} of {totalSteps}
          </span>
          <Button
            {...closeProps}
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="mb-4">
          {step.content}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            {index > 0 && (
              <Button
                {...backProps}
                variant="outline"
                size="sm"
                className="h-8"
              >
                <ArrowLeft className="h-3 w-3 mr-1" />
                Back
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            {continuous && (
              <Button
                {...skipProps}
                variant="ghost"
                size="sm"
                className="h-8 text-muted-foreground hover:text-foreground"
              >
                <SkipForward className="h-3 w-3 mr-1" />
                Skip
              </Button>
            )}
            <Button
              {...primaryProps}
              size="sm"
              className="h-8"
            >
              {continuous ? (
                <>
                  Next
                  <ArrowRight className="h-3 w-3 ml-1" />
                </>
              ) : (
                'Got it!'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};